import { ApiError } from '@/shared/api/errors';

/** The loaded SDK, as the app uses it. */
export interface BrowserReporter {
  send(error: unknown): void;
  /** Who is signed in, as an ID only (no names or emails in error reports). */
  setUser(id: string | undefined): void;
}

export interface ErrorReportingOptions {
  readonly dsn: string | undefined;
  readonly release: string | undefined;
  readonly environment: string;
}

const MAX_PENDING = 10;

let enabled = false;
let reporter: BrowserReporter | undefined;
let userId: string | undefined;
const pending: unknown[] = [];

/**
 * Browser errors to GlitchTip (ADR-0010, NFR-6). The SDK is its own chunk, loaded after the first
 * render and only when a DSN is set at build time, so it costs the first load nothing (NFR-2). The DSN
 * points at our origin (`/glitchtip/…`, proxied by Caddy), so the CSP keeps `connect-src 'self'`.
 */
export async function startErrorReporting(options: ErrorReportingOptions): Promise<void> {
  if (!options.dsn || enabled) return;
  enabled = true;
  const { createBrowserReporter } = await import('./error-reporting-client');
  reporter = createBrowserReporter({ ...options, dsn: options.dsn });
  reporter.setUser(userId);
  for (const error of pending.splice(0)) reporter.send(error);
}

export function setErrorReportingUser(id: string | undefined): void {
  userId = id;
  reporter?.setUser(id);
}

/**
 * Reports an error the app caught itself (a page that failed to render); uncaught ones arrive through
 * the SDK's global handlers. Failed API calls are not reported here: the server reports its own faults,
 * and the rest are not bugs (4xx, offline).
 */
export function reportError(error: unknown): void {
  if (!enabled || error instanceof ApiError) return;
  if (reporter) reporter.send(error);
  else if (pending.length < MAX_PENDING) pending.push(error);
}

/** Tests only: back to the state of a fresh page. */
export function resetErrorReportingForTests(): void {
  enabled = false;
  reporter = undefined;
  userId = undefined;
  pending.length = 0;
}
